import { useRef, useState } from 'react';

import type { Locale } from '@eneco/common/application';
import logger from '@eneco/common/log';
import { useAsync } from 'react-use';

import { Dictionary } from './types';

type Fn = Dictionary | ((locale: Locale) => Promise<Dictionary>);

type DictionaryState = Dictionary | null;

const useDictionary = (
  dictionaryOrFn: Fn,
  locale: Locale,
): {
  dictionary: DictionaryState;
  isLoading: boolean;
} => {
  const isAsyncLoader = typeof dictionaryOrFn === 'function';
  const [asyncDictionary, setAsyncDictionary] = useState<DictionaryState>(null);
  const [isLoading, setIsLoading] = useState<boolean>(isAsyncLoader);

  // Keep a stable ref to avoid re-firing useAsync when callers pass an inline function
  const loaderRef = useRef(dictionaryOrFn);
  loaderRef.current = dictionaryOrFn;

  useAsync(async () => {
    const loader = loaderRef.current;
    if (typeof loader === 'function') {
      try {
        setIsLoading(true);
        const response = await loader(locale);
        // Accept __esModule and POJOs
        setAsyncDictionary((response.default ?? response) as Dictionary);
        setIsLoading(false);
      } catch (error) {
        // Only a network error could take us here (Webpack dev mode throws for incorrect paths)
        logger.error('VpvrHU', 'Error fetching dictionary', error);
        setIsLoading(false);
      }
    }
  }, [locale]);

  if (!isAsyncLoader) {
    return { dictionary: dictionaryOrFn as Dictionary, isLoading: false };
  }

  return { isLoading, dictionary: asyncDictionary };
};

export default useDictionary;
